/**
 * Place Ratings Service
 * 
 * Handles user ratings and reviews for places, trending places discovery
 */

import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit,
  Timestamp,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { functions, httpsCallable } from '@/lib/functions';
import type { LocationCoordinates } from './location';

export type PlaceRating = {
  id: string;
  placeId: string; // Unique identifier for place (address or coordinates hash)
  placeName: string;
  coordinates: LocationCoordinates;
  userId: string;
  rating: number; // 1-5
  review?: string;
  photos?: string[];
  tripId?: string; // Trip where user visited
  category?: string;
  createdAt: Date;
  updatedAt: Date;
};

export type PlaceRatingAggregate = {
  placeId: string;
  placeName: string;
  coordinates: LocationCoordinates;
  averageRating: number;
  totalRatings: number;
  ratingDistribution: { [key: number]: number }; // {1: 5, 2: 2, 3: 10, 4: 25, 5: 58}
  lastUpdated: Date;
  category?: string;
  address?: string;
  city?: string; // City name for filtering
  googlePlaceId?: string; // Cached Google Place ID
  googleRating?: number; // Google's rating
  googleRatingCount?: number; // Google's review count
  lastSyncedAt?: Date; // When last synced with Google
};

type FirestorePlaceRating = Omit<PlaceRating, 'id' | 'createdAt' | 'updatedAt' | 'coordinates'> & {
  createdAt: Timestamp;
  updatedAt: Timestamp;
  coordinates: {
    latitude: number;
    longitude: number;
  };
};

type FirestorePlaceRatingAggregate = Omit<PlaceRatingAggregate, 'lastUpdated' | 'coordinates' | 'lastSyncedAt'> & {
  lastUpdated: Timestamp;
  coordinates: {
    latitude: number;
    longitude: number;
  };
  city?: string;
  googlePlaceId?: string;
  googleRating?: number;
  googleRatingCount?: number;
  lastSyncedAt?: Timestamp;
};

const PLACE_RATINGS_COLLECTION = 'placeRatings';
const PLACE_RATINGS_AGGREGATES_COLLECTION = 'placeRatingAggregates';

type SyncPlaceRatingsRequest = {
  placeId: string;
};

type SyncPlaceRatingsResponse = {
  success: boolean;
  placeId: string;
  rating?: number;
  userRatingCount?: number;
};

/**
 * Generate a unique place ID from coordinates or address
 */
export const generatePlaceId = (
  coordinates: LocationCoordinates,
  placeName?: string
): string => {
  // Use coordinates hash for unique ID
  const lat = coordinates.latitude.toFixed(6);
  const lng = coordinates.longitude.toFixed(6);
  return `${lat},${lng}`;
};

/**
 * Rate a place
 */
export const ratePlace = async (
  userId: string,
  placeData: {
    placeId: string;
    placeName: string;
    coordinates: LocationCoordinates;
    rating: number;
    review?: string;
    photos?: string[];
    tripId?: string;
    category?: string;
  }
): Promise<void> => {
  try {
    console.log('[PlaceRatings] Rating place:', placeData.placeId);

    const ratingsRef = collection(db, PLACE_RATINGS_COLLECTION);
    
    // Check if user already rated this place
    const existingQuery = query(
      ratingsRef,
      where('placeId', '==', placeData.placeId),
      where('userId', '==', userId)
    );
    const existingSnapshot = await getDocs(existingQuery);

    const batch = writeBatch(db);

    if (!existingSnapshot.empty) {
      // Update existing rating
      const existingDoc = existingSnapshot.docs[0];
      const firestoreRating: FirestorePlaceRating = {
        placeId: placeData.placeId,
        placeName: placeData.placeName,
        coordinates: {
          latitude: placeData.coordinates.latitude,
          longitude: placeData.coordinates.longitude,
        },
        userId,
        rating: placeData.rating,
        review: placeData.review,
        photos: placeData.photos,
        tripId: placeData.tripId,
        category: placeData.category,
        updatedAt: serverTimestamp() as Timestamp,
        createdAt: existingDoc.data().createdAt, // Keep original creation date
      };

      batch.set(existingDoc.ref, firestoreRating);
    } else {
      // Create new rating
      const newRatingRef = doc(ratingsRef);
      const firestoreRating: FirestorePlaceRating = {
        placeId: placeData.placeId,
        placeName: placeData.placeName,
        coordinates: {
          latitude: placeData.coordinates.latitude,
          longitude: placeData.coordinates.longitude,
        },
        userId,
        rating: placeData.rating,
        review: placeData.review,
        photos: placeData.photos,
        tripId: placeData.tripId,
        category: placeData.category,
        createdAt: serverTimestamp() as Timestamp,
        updatedAt: serverTimestamp() as Timestamp,
      };

      batch.set(newRatingRef, firestoreRating);
    }

    await batch.commit();
    await syncPlaceRatingsAggregate(placeData.placeId).catch((error) => {
      // Don't fail a user rating if aggregate sync fails; data can be recomputed later.
      console.warn('[PlaceRatings] Aggregate sync failed after rating commit:', error);
    });
    console.log('[PlaceRatings] Place rated successfully');
  } catch (error) {
    console.error('[PlaceRatings] Error rating place:', error);
    throw error;
  }
};

/**
 * Recompute aggregate ratings via trusted backend callable.
 */
const syncPlaceRatingsAggregate = async (placeId: string): Promise<void> => {
  const callable = httpsCallable<SyncPlaceRatingsRequest, SyncPlaceRatingsResponse>(
    functions,
    'syncPlaceRatings'
  );
  await callable({ placeId });
};

/**
 * Get aggregated rating for a place
 */
export const getPlaceRating = async (
  placeId: string
): Promise<PlaceRatingAggregate | null> => {
  try {
    const aggregatesRef = collection(db, PLACE_RATINGS_AGGREGATES_COLLECTION);
    const aggregateDoc = await getDoc(doc(aggregatesRef, placeId));

    if (!aggregateDoc.exists()) {
      return null;
    }

    const data = aggregateDoc.data() as FirestorePlaceRatingAggregate;
    return {
      placeId: data.placeId,
      placeName: data.placeName,
      coordinates: {
        latitude: data.coordinates.latitude,
        longitude: data.coordinates.longitude,
      },
      averageRating: data.averageRating,
      totalRatings: data.totalRatings,
      ratingDistribution: data.ratingDistribution,
      lastUpdated: data.lastUpdated.toDate(),
      category: data.category,
      address: data.address,
      city: data.city,
    };
  } catch (error) {
    console.error('[PlaceRatings] Error getting place rating:', error);
    return null;
  }
};

/**
 * Get user's rating for a place
 */
export const getUserPlaceRating = async (
  placeId: string,
  userId: string
): Promise<PlaceRating | null> => {
  try {
    const ratingsRef = collection(db, PLACE_RATINGS_COLLECTION);
    const ratingQuery = query(
      ratingsRef,
      where('placeId', '==', placeId),
      where('userId', '==', userId)
    );
    const snapshot = await getDocs(ratingQuery);

    if (snapshot.empty) {
      return null;
    }

    const doc = snapshot.docs[0];
    const data = doc.data() as FirestorePlaceRating;
    return {
      id: doc.id,
      placeId: data.placeId,
      placeName: data.placeName,
      coordinates: {
        latitude: data.coordinates.latitude,
        longitude: data.coordinates.longitude,
      },
      userId: data.userId,
      rating: data.rating,
      review: data.review,
      photos: data.photos,
      tripId: data.tripId,
      category: data.category,
      createdAt: data.createdAt.toDate(),
      updatedAt: data.updatedAt.toDate(),
    };
  } catch (error) {
    console.error('[PlaceRatings] Error getting user rating:', error);
    return null;
  }
};

/**
 * Extract city name from destination string
 * Handles formats like "Paris, France", "Tokyo", "New York, NY, USA"
 */
const extractCityFromDestination = (destination: string): string => {
  // Remove common suffixes
  const cleaned = destination.trim();
  const parts = cleaned.split(',').map(s => s.trim());
  
  // Usually city is the first part
  return parts[0];
};

/**
 * Calculate distance between two coordinates (Haversine formula)
 */
const calculateDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number => {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) *
    Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/**
 * Get trending places for a destination
 * Supports filtering by destination name or proximity to coordinates
 */
export const getTrendingPlaces = async (
  destination: string,
  limitCount: number = 10,
  options?: {
    proximity?: LocationCoordinates;
    maxDistanceKm?: number;
    useCache?: boolean; // Whether to use cache (default: true)
  }
): Promise<PlaceRatingAggregate[]> => {
  try {
    // Check cache first
    if (options?.useCache !== false) {
      const { getCachedTrendingPlaces, cacheTrendingPlaces } = await import('@/utils/trendingPlacesCache');
      const cached = await getCachedTrendingPlaces(destination, options?.proximity);
      if (cached) {
        console.log('[PlaceRatings] Using cached trending places for:', destination);
        return cached.slice(0, limitCount);
      }
    }

    console.log('[PlaceRatings] Getting trending places for:', destination, options);

    const aggregatesRef = collection(db, PLACE_RATINGS_AGGREGATES_COLLECTION);
    
    // Extract city name from destination for filtering
    const cityName = extractCityFromDestination(destination);
    const cityLower = cityName.toLowerCase();
    
    // Query places with at least 3 ratings
    let trendingQuery = query(
      aggregatesRef,
      where('totalRatings', '>=', 3),
      orderBy('totalRatings', 'desc'),
      orderBy('averageRating', 'desc'),
      limit(limitCount * 2) // Get more to filter by city/proximity
    );

    const snapshot = await getDocs(trendingQuery);
    let places: PlaceRatingAggregate[] = [];

    snapshot.forEach((doc) => {
      const data = doc.data() as FirestorePlaceRatingAggregate;
      const place: PlaceRatingAggregate = {
        placeId: data.placeId,
        placeName: data.placeName,
        coordinates: {
          latitude: data.coordinates.latitude,
          longitude: data.coordinates.longitude,
        },
        averageRating: data.averageRating,
        totalRatings: data.totalRatings,
        ratingDistribution: data.ratingDistribution,
        lastUpdated: data.lastUpdated.toDate(),
        category: data.category,
        address: data.address,
        city: data.city,
      };
      places.push(place);
    });

    // Filter by city name if available
    if (cityName) {
      places = places.filter((place) => {
        // Check if city matches
        if (place.city) {
          return place.city.toLowerCase().includes(cityLower) || 
                 cityLower.includes(place.city.toLowerCase());
        }
        
        // Fallback: check if address or place name contains city
        const addressLower = (place.address || place.placeName || '').toLowerCase();
        return addressLower.includes(cityLower) || cityLower.includes(addressLower.split(',')[0]);
      });
    }

    // Filter by proximity if coordinates provided
    if (options?.proximity && options?.maxDistanceKm) {
      places = places.filter((place) => {
        const distance = calculateDistance(
          options.proximity!.latitude,
          options.proximity!.longitude,
          place.coordinates.latitude,
          place.coordinates.longitude
        );
        return distance <= options.maxDistanceKm!;
      });
    }

    // Sort by rating score (combination of average rating and total ratings)
    places.sort((a, b) => {
      const scoreA = a.averageRating * Math.log(a.totalRatings + 1);
      const scoreB = b.averageRating * Math.log(b.totalRatings + 1);
      return scoreB - scoreA;
    });

    // Limit results
    places = places.slice(0, limitCount);

    // Cache results
    if (options?.useCache !== false && places.length > 0) {
      const { cacheTrendingPlaces } = await import('@/utils/trendingPlacesCache');
      await cacheTrendingPlaces(destination, places, options?.proximity);
    }

    console.log(`[PlaceRatings] Found ${places.length} trending places for ${destination}`);
    return places;
  } catch (error) {
    console.error('[PlaceRatings] Error getting trending places:', error);
    return [];
  }
};

/**
 * Get trending places by category
 */
export const getTrendingPlacesByCategory = async (
  category: string,
  limitCount: number = 10
): Promise<PlaceRatingAggregate[]> => {
  try {
    const aggregatesRef = collection(db, PLACE_RATINGS_AGGREGATES_COLLECTION);
    const categoryQuery = query(
      aggregatesRef,
      where('category', '==', category),
      where('totalRatings', '>=', 2),
      orderBy('totalRatings', 'desc'),
      orderBy('averageRating', 'desc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(categoryQuery);
    const places: PlaceRatingAggregate[] = [];

    snapshot.forEach((doc) => {
      const data = doc.data() as FirestorePlaceRatingAggregate;
      places.push({
        placeId: data.placeId,
        placeName: data.placeName,
        coordinates: {
          latitude: data.coordinates.latitude,
          longitude: data.coordinates.longitude,
        },
        averageRating: data.averageRating,
        totalRatings: data.totalRatings,
        ratingDistribution: data.ratingDistribution,
        lastUpdated: data.lastUpdated.toDate(),
        category: data.category,
        address: data.address,
        city: data.city,
      });
    });

    return places;
  } catch (error) {
    console.error('[PlaceRatings] Error getting trending places by category:', error);
    return [];
  }
};

/**
 * Get recent ratings for a place
 */
export const getPlaceRecentRatings = async (
  placeId: string,
  limitCount: number = 10
): Promise<PlaceRating[]> => {
  try {
    const ratingsRef = collection(db, PLACE_RATINGS_COLLECTION);
    const ratingsQuery = query(
      ratingsRef,
      where('placeId', '==', placeId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(ratingsQuery);
    const ratings: PlaceRating[] = [];

    snapshot.forEach((doc) => {
      const data = doc.data() as FirestorePlaceRating;
      ratings.push({
        id: doc.id,
        placeId: data.placeId,
        placeName: data.placeName,
        coordinates: {
          latitude: data.coordinates.latitude,
          longitude: data.coordinates.longitude,
        },
        userId: data.userId,
        rating: data.rating,
        review: data.review,
        photos: data.photos,
        tripId: data.tripId,
        category: data.category,
        createdAt: data.createdAt.toDate(),
        updatedAt: data.updatedAt.toDate(),
      });
    });

    return ratings;
  } catch (error) {
    console.error('[PlaceRatings] Error getting recent ratings:', error);
    return [];
  }
};

